/**
 * Pure validation and draft-merging utilities for Ghostwriter form fields.
 */

import type { DraftFieldValue } from "./GhostwriterStore";

import {
  buildInstancedFieldId,
  FieldType,
  type ScheduleApplyFormFieldBase,
  type ScheduleApplyFormFieldCondition,
  type ScheduleApplyFormFieldGroup,
  type ScheduleApplyOptionsFormField,
  type ScheduleApplyUnboundFormField,
  type ValidationInputType,
} from "../models/ScheduleApplyFormField";

import parsePhoneNumber from "libphonenumber-js";

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isUnboundField(
  field: ScheduleApplyFormFieldBase,
): field is ScheduleApplyUnboundFormField {
  return (
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    "response" in field && "responseValue" in (field as ScheduleApplyUnboundFormField).response
  );
}

export function isOptionsField(
  field: ScheduleApplyFormFieldBase,
): field is ScheduleApplyOptionsFormField {
  return (
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    "response" in field && "responseValues" in (field as ScheduleApplyOptionsFormField).response
  );
}

// ---------------------------------------------------------------------------
// Format validation
// ---------------------------------------------------------------------------

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Validates hostname format: alphanumeric segments (hyphens allowed mid-segment)
 * separated by dots, ending with an alphabetic TLD of at least 2 characters.
 */
const VALID_HOSTNAME = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

function isValidUrl(value: string): boolean {
  try {
    const urlString = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    const url = new URL(urlString);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;

    const { hostname } = url;
    if (!VALID_HOSTNAME.test(hostname)) return false;

    if (/^www\./i.test(hostname) && !hostname.slice(4).includes(".")) return false;

    return true;
  } catch {
    return false;
  }
}

function isValidPhone(value: string): boolean {
  try {
    const parsed = parsePhoneNumber(value, "US");
    return parsed?.isValid() === true;
  } catch {
    return false;
  }
}

/**
 * Must contain exactly 9 digits and no other characters.
 */
function isValidSocialSecurityNumberFormat(value: string): boolean {
  return /^\d{9}$/.test(value);
}

function isValidPostalCodeDigitsOnly(value: string): boolean {
  return /^\d+$/.test(value);
}

function isFieldFormatValid(
  fieldType: FieldType,
  value: string,
  validationInputType?: ValidationInputType | null,
): boolean {
  if (
    fieldType === FieldType.SocialSecurityNumber ||
    validationInputType === "SOCIAL_SECURITY_NUMBER"
  ) {
    return isValidSocialSecurityNumberFormat(value);
  }
  if (fieldType === FieldType.PostalCode || validationInputType === "POSTAL_CODE") {
    return isValidPostalCodeDigitsOnly(value);
  }

  switch (fieldType) {
    case FieldType.Email:
      return isValidEmail(value);
    case FieldType.Tel:
      return isValidPhone(value);
    case FieldType.Url:
      return isValidUrl(value);
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------

/**
 * Reads the "current" response value of a field for the purpose of
 * evaluating a {@link ScheduleApplyFormFieldCondition} against it.
 *
 * - For unbound fields, returns the response string.
 * - For options fields, returns the array of selected option values.
 * - For any other field shape (e.g. legacy / unknown), returns an empty
 *   string so it never satisfies an `EQ` condition.
 *
 * Callers should pass the field *as currently hydrated* (i.e. with draft
 * values already merged into `field.response`). The visibility evaluator
 * never reaches into a draft map directly; it only inspects the field
 * objects given to it. This keeps visibility a pure function of its inputs
 * and makes it trivial to evaluate against either a hydrated instance
 * (renderer / submit) or the raw committed groups (`collectInvalidFields`).
 */
function readResponseForCondition(field: ScheduleApplyFormFieldBase): string | string[] {
  if (isUnboundField(field)) return field.response.responseValue;
  if (isOptionsField(field)) return field.response.responseValues;
  return "";
}

/**
 * Returns true when `value` "matches" `condition.value`.
 *
 * - For a string `value` (unbound field), exact equality.
 * - For an array `value` (options field), membership: any selected option
 *   equal to `condition.value` is treated as a match. This generalizes
 *   single- and multi-select parents — a `RADIO`/`SELECT` parent has at
 *   most one element so this collapses to equality, and a `CHECKBOX` parent
 *   matches if the user has ticked the gating option.
 */
function conditionValueMatches(value: string | string[], conditionValue: string): boolean {
  if (Array.isArray(value)) return value.includes(conditionValue);
  return value === conditionValue;
}

/**
 * Evaluates a single `condition` against the current response of its parent.
 * Returns `true` (visible) when the condition is satisfied, `false`
 * otherwise. When the parent cannot be found among the provided siblings
 * (e.g. malformed condition pointing at an unknown `fieldName`), the field
 * is treated as visible — this errs on the side of *showing* a field over
 * silently dropping data.
 */
function isConditionSatisfied(
  condition: ScheduleApplyFormFieldCondition,
  siblingFields: ReadonlyArray<ScheduleApplyFormFieldBase>,
): boolean {
  const parent = siblingFields.find((f) => f.fieldName === condition.fieldName);
  if (!parent) return true;

  const parentValue = readResponseForCondition(parent);
  const matches = conditionValueMatches(parentValue, condition.value);
  return condition.operator === "NE" ? !matches : matches;
}

/**
 * Returns true when `field` should be displayed given the current state of
 * its sibling fields. Fields without a `condition` are always visible.
 *
 * `siblingFields` must be the fields that share the same group *instance*
 * as `field` — for repeatable groups, callers must scope to a single
 * instance's hydrated fields rather than the whole group. This keeps the
 * dependency search local and unambiguous (two instances of the same group
 * each have their own parent value).
 */
export function isFieldVisible(
  field: ScheduleApplyFormFieldBase,
  siblingFields: ReadonlyArray<ScheduleApplyFormFieldBase>,
): boolean {
  if (!field.condition) return true;
  return isConditionSatisfied(field.condition, siblingFields);
}

/**
 * Hydrates every field in `group.formFields` for the given instance suffix
 * by merging the draft values into each field's `response`. The first
 * (primary) instance uses bare draft keys; non-primary instances use
 * `${field.id}#${suffix}` keys. The result is suitable for passing to
 * {@link isFieldVisible} as `siblingFields`.
 */
export function hydrateGroupInstanceFields(
  group: ScheduleApplyFormFieldGroup,
  instanceSuffix: string | null,
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
): ScheduleApplyFormFieldBase[] {
  return group.formFields.map((f) => applyDraftToField(f, fieldUpdates, instanceSuffix));
}

// ---------------------------------------------------------------------------
// Field validation
// ---------------------------------------------------------------------------

/**
 * Returns true when a field's value is valid. Checks both emptiness (for
 * required fields) and format (for Email, Tel, Url, SSN, postal, and legacy
 * `validationInputType` masks).
 *
 * When a draftValue is provided it takes precedence over the committed
 * response; otherwise the committed response is checked.
 *
 * Format validation applies to non-empty values regardless of whether the
 * field is required — an optional email field with "not-an-email" is invalid.
 */
export function isFieldValid(
  field: ScheduleApplyFormFieldBase,
  draftValue?: DraftFieldValue,
): boolean {
  if (draftValue === undefined) {
    if (isUnboundField(field)) {
      const value = field.response.responseValue;
      if (value === "") return !field.required;
      return isFieldFormatValid(field.fieldType, value, field.validationInputType);
    }
    if (isOptionsField(field)) {
      return !field.required || field.response.responseValues.length > 0;
    }
    return true;
  }

  if (typeof draftValue === "string") {
    const trimmed = draftValue.trim();
    if (trimmed === "") return !field.required;
    return isFieldFormatValid(field.fieldType, trimmed, field.validationInputType);
  }

  return !field.required || draftValue.length > 0;
}

/**
 * Walks all form field groups (including nested) and returns the ordered
 * list of required fields whose committed value is empty.
 *
 * Conditional fields whose `condition` is *not* satisfied by the committed
 * sibling responses are skipped — they're effectively hidden from the user
 * so we don't want to count them as missing required answers. Sibling
 * lookup is scoped to the parent group; conditions never cross groups.
 */
export function collectInvalidFields(
  groups: ScheduleApplyFormFieldGroup[],
): ScheduleApplyFormFieldBase[] {
  const result: ScheduleApplyFormFieldBase[] = [];

  for (const group of groups) {
    for (const field of group.formFields) {
      if (!isFieldVisible(field, group.formFields)) continue;
      if (!isFieldValid(field)) {
        result.push(field);
      }
    }
    if (group.nestedFormFieldGroup) {
      result.push(...collectInvalidFields([group.nestedFormFieldGroup]));
    }
  }

  return result;
}

/**
 * Returns true when every field in every group passes validation against the
 * current draft values, walking *every* instance for repeatable groups.
 *
 * `groupInstances` maps each group ID to its ordered list of instance IDs.
 * The first instance always uses bare `field.id` keys; subsequent instances
 * use `${field.id}#${instanceId}`. When omitted, every group is treated as
 * having a single primary instance (legacy behavior).
 */
export function areAllFieldsValid(
  groups: ScheduleApplyFormFieldGroup[],
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
  groupInstances?: ReadonlyMap<string, ReadonlyArray<string>>,
): boolean {
  for (const group of groups) {
    const instances = groupInstances?.get(group.groupId);
    const instanceCount = instances?.length ?? 1;
    for (let i = 0; i < instanceCount; i++) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const suffix = i === 0 ? null : (instances as ReadonlyArray<string>)[i];
      // Hydrate the *whole* instance once so visibility evaluation
      // sees the live draft values for sibling parents.
      const hydratedSiblings = hydrateGroupInstanceFields(group, suffix, fieldUpdates);
      for (let f = 0; f < group.formFields.length; f++) {
        const field = group.formFields[f];
        if (!isFieldVisible(hydratedSiblings[f], hydratedSiblings)) continue;
        const key = buildInstancedFieldId(field.id, suffix);
        if (!isFieldValid(field, fieldUpdates.get(key))) {
          return false;
        }
      }
    }
    if (group.nestedFormFieldGroup) {
      if (!areAllFieldsValid([group.nestedFormFieldGroup], fieldUpdates, groupInstances)) {
        return false;
      }
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Confirm-email cross-field validation
// ---------------------------------------------------------------------------

/**
 * Detector for "Confirm Email" fields.
 *
 * Ghostwriter's OneGraph schema exposes `confirmsFieldName` on every form
 * field: when populated, its value is the `fieldName` of the sibling
 * form field that *this* field re-asks the user to confirm (e.g. for a
 * "Re-enter email" pair, the confirm field's `confirmsFieldName` is the
 * primary email field's `fieldName`).
 *
 * We treat any Email-typed field with a non-empty `confirmsFieldName` as a
 * confirm-email field. The Email-type guard keeps this function specific
 * to the email-confirmation flow; non-email confirmation fields (if any
 * are ever introduced) would need their own helper.
 */
export function isConfirmEmailField(field: ScheduleApplyFormFieldBase): boolean {
  if (field.fieldType !== FieldType.Email) return false;
  const target = field.confirmsFieldName;
  return target != null && target !== "";
}

/**
 * Given a confirm-email field, returns the visible Email sibling whose
 * `fieldName` matches `confirmField.confirmsFieldName`. Returns
 * `undefined` when the group contains no matching primary email field
 * (e.g. the referenced field was hidden by a condition, the schema points
 * at an unknown `fieldName`, or `confirmsFieldName` is null) — in which
 * case the confirmation field is left to validate by its own format rule
 * only.
 *
 * `siblingFields` must be the hydrated fields of a single group instance
 * (matching the `siblingFields` contract used by {@link isFieldVisible}).
 */
export function findEmailFieldToConfirm(
  confirmField: ScheduleApplyFormFieldBase,
  siblingFields: ReadonlyArray<ScheduleApplyFormFieldBase>,
): ScheduleApplyFormFieldBase | undefined {
  const targetName = confirmField.confirmsFieldName;
  if (targetName == null || targetName === "") return undefined;
  for (const candidate of siblingFields) {
    if (candidate.id === confirmField.id) continue;
    if (candidate.fieldName !== targetName) continue;
    if (candidate.fieldType !== FieldType.Email) continue;
    if (!isFieldVisible(candidate, siblingFields)) continue;
    return candidate;
  }
  return undefined;
}

/**
 * Reads the unbound string response of an already-hydrated field. Returns
 * `''` for non-unbound shapes so callers don't have to special-case them.
 */
function readHydratedUnboundValue(field: ScheduleApplyFormFieldBase): string {
  return isUnboundField(field) ? field.response.responseValue : "";
}

/**
 * Compares two trimmed email values for confirmation-field purposes.
 * Comparison is case-insensitive so users aren't blocked by casing
 * differences (e.g. autofill vs manual entry).
 */
export function emailsMatchForConfirmation(confirmValue: string, targetValue: string): boolean {
  return confirmValue.toLowerCase() === targetValue.toLowerCase();
}

/**
 * Walks every group instance and returns the set of instanced field IDs of
 * confirm-email fields whose value mismatches the matched primary email
 * field's value. A pair only counts as "mismatched" when:
 *
 *  - both fields are visible (per `isFieldVisible`);
 *  - both fields have a non-empty trimmed value (empty values are handled
 *    by the existing required / format checks — flagging an empty confirm
 *    field as a "mismatch" would shadow the more specific "Required" error);
 *  - the trimmed values are not equal (case-insensitive).
 *
 * The returned IDs use the same `${fieldId}` / `${fieldId}#${suffix}` shape
 * as `fieldValidationErrors`, so callers can union them directly.
 *
 * `groupInstances` mirrors the shape used by {@link areAllFieldsValid}; the
 * first instance always uses bare `field.id` keys, subsequent instances use
 * `${field.id}#${instanceId}`. When omitted, every group is treated as a
 * single primary instance.
 */
export function collectEmailConfirmationMismatches(
  groups: ReadonlyArray<ScheduleApplyFormFieldGroup>,
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
  groupInstances?: ReadonlyMap<string, ReadonlyArray<string>>,
): Set<string> {
  const mismatches = new Set<string>();

  function walk(groups: ReadonlyArray<ScheduleApplyFormFieldGroup>) {
    for (const group of groups) {
      const instances = groupInstances?.get(group.groupId);
      const instanceCount = instances?.length ?? 1;
      for (let i = 0; i < instanceCount; i++) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        const suffix = i === 0 ? null : (instances as ReadonlyArray<string>)[i];
        const hydrated = hydrateGroupInstanceFields(group, suffix, fieldUpdates);
        for (let f = 0; f < hydrated.length; f++) {
          const confirm = hydrated[f];
          if (!isConfirmEmailField(confirm)) continue;
          if (!isFieldVisible(confirm, hydrated)) continue;

          const target = findEmailFieldToConfirm(confirm, hydrated);
          if (!target) continue;

          const confirmValue = readHydratedUnboundValue(confirm).trim();
          const targetValue = readHydratedUnboundValue(target).trim();
          if (confirmValue === "" || targetValue === "") continue;
          if (emailsMatchForConfirmation(confirmValue, targetValue)) continue;

          mismatches.add(buildInstancedFieldId(group.formFields[f].id, suffix));
        }
      }
      if (group.nestedFormFieldGroup) {
        walk([group.nestedFormFieldGroup]);
      }
    }
  }

  walk(groups);
  return mismatches;
}

// ---------------------------------------------------------------------------
// Group inspection
// ---------------------------------------------------------------------------

/**
 * Returns true if any field in the group (or its nested group) has a
 * non-empty committed response value.
 */
export function groupHasAnswers(group: ScheduleApplyFormFieldGroup): boolean {
  for (const field of group.formFields) {
    if (isUnboundField(field) && field.response.responseValue !== "") return true;
    if (isOptionsField(field) && field.response.responseValues.length > 0) return true;
  }
  if (group.nestedFormFieldGroup) {
    return groupHasAnswers(group.nestedFormFieldGroup);
  }
  return false;
}

/**
 * Returns true if a specific *instance* of the group has any non-empty draft
 * value. The first instance falls back to the committed `field.response`
 * values when no draft entry is present.
 */
export function groupInstanceHasAnswers(
  group: ScheduleApplyFormFieldGroup,
  instanceSuffix: string | null,
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
): boolean {
  for (const field of group.formFields) {
    const key = buildInstancedFieldId(field.id, instanceSuffix);
    const draftValue = fieldUpdates.get(key);

    if (draftValue !== undefined) {
      if (typeof draftValue === "string" && draftValue !== "") return true;
      if (Array.isArray(draftValue) && draftValue.length > 0) return true;
      continue;
    }

    // No draft entry. Only the primary instance falls back to committed values;
    // non-primary instances default to empty.
    if (instanceSuffix != null) continue;
    if (isUnboundField(field) && field.response.responseValue !== "") return true;
    if (isOptionsField(field) && field.response.responseValues.length > 0) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Draft → model merging
// ---------------------------------------------------------------------------

/**
 * Produces a copy of the form field with its response value replaced by the
 * draft value (when present). When `instanceSuffix` is provided, the lookup
 * uses `${field.id}#${instanceSuffix}` instead of the bare field ID — this
 * supports per-instance hydration for repeatable groups. Empty draft values
 * (`""` or `[]`) are treated as deliberate clears and overwrite the committed
 * value, but a *missing* draft entry leaves the field untouched.
 */
export function applyDraftToField(
  field: ScheduleApplyFormFieldBase,
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
  instanceSuffix?: string | null,
): ScheduleApplyFormFieldBase {
  const key = buildInstancedFieldId(field.id, instanceSuffix);
  const draftValue = fieldUpdates.get(key);
  if (draftValue === undefined) {
    // For non-primary instances with no draft entry yet, return the field
    // with an empty response so the UI starts blank rather than echoing
    // the committed value of the primary instance.
    if (instanceSuffix != null) {
      if (isUnboundField(field)) {
        const cleared: ScheduleApplyUnboundFormField = {
          ...field,
          response: { ...field.response, responseValue: "" },
        };
        return cleared;
      }
      if (isOptionsField(field)) {
        const cleared: ScheduleApplyOptionsFormField = {
          ...field,
          response: { ...field.response, responseValues: [] },
        };
        return cleared;
      }
    }
    return field;
  }

  if (isUnboundField(field) && typeof draftValue === "string") {
    const updated: ScheduleApplyUnboundFormField = {
      ...field,
      response: { ...field.response, responseValue: draftValue },
    };
    return updated;
  }

  if (isOptionsField(field) && Array.isArray(draftValue)) {
    const updated: ScheduleApplyOptionsFormField = {
      ...field,
      response: { ...field.response, responseValues: draftValue },
    };
    return updated;
  }

  return field;
}

/**
 * Applies draft values to all fields in the given groups (including nested
 * groups), returning new group objects with merged responses. Hydrates
 * primary-instance values only — repeatable-group renderers fan out to
 * additional instances via `applyDraftToField` with an explicit suffix.
 */
export function applyDraftToGroups(
  groups: ScheduleApplyFormFieldGroup[],
  fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
): ScheduleApplyFormFieldGroup[] {
  return groups.map((group) => ({
    ...group,
    formFields: group.formFields.map((f) => applyDraftToField(f, fieldUpdates)),
    nestedFormFieldGroup: group.nestedFormFieldGroup
      ? applyDraftToGroups([group.nestedFormFieldGroup], fieldUpdates)[0]
      : null,
  }));
}
